import React from "react";
import { Search, SlidersHorizontal, Plus } from "lucide-react";
import { CategoryFilter } from "./UI";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import { useUser } from "../context/user";
import EventRow from "./EventRow";
import styles from "./Home.module.css";

/*
 * Renders the primary dashboard view including the user profile avatar, search bar,
 * category filters, featured event banner, and personalized horizontal scrollable
 * event rows, plus an all-events section at the bottom.
 */
export default function HomeScreen({
  selectedCategory,
  setSelectedCategory,
  searchQuery,
  setSearchQuery,
  featuredEvent,
  filteredEvents = [],
  filteredCommunities = [],
  firstRowEvents = [],
  secondRowEvents = [],
  thirdRowEvents = [],
  allEvents = [],
  firstRowTitle = "Recommended for you",
  secondRowTitle = "Popular in your area",
  thirdRowTitle = "More suggestions",
  allEventsTitle = "All Events",
  onSelectEvent,
  onOpenCreateEvent,
  onNavigateProfile,
  onSelectCommunity,
  onToggleJoinEvent,
  joinedEvents = [],
  hideHeader = false,
}) {
  const { userData } = useUser();

  const isFeaturedJoined = featuredEvent
    ? joinedEvents.includes(featuredEvent.id)
    : false;

  // Check if the user is actively filtering by category or search query
  const isFiltering =
    selectedCategory !== "For You" ||
    (searchQuery && searchQuery.trim() !== "");

  return (
    <div className={styles.homeContainer}>
      {/* Top Header */}
      {!hideHeader && (
        <div className={styles.topHeader}>
          <div
            onClick={onNavigateProfile}
            className={styles.userAvatarWrapper}
            title="View Profile"
          >
            <img
              src={getAvatarUrl(userData)}
              alt="User Avatar"
              className={styles.avatarImg}
            />
          </div>

          <div className={styles.searchBarContainer}>
            <Search size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Look for activities or communities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <button
            onClick={onOpenCreateEvent}
            className={styles.filterBtn}
            title="Create Event"
          >
            <SlidersHorizontal size={18} />
          </button>
        </div>
      )}

      {/* Horizontal category filter pills */}
      <CategoryFilter
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      {/* Communities Search Results Section */}
      {searchQuery && filteredCommunities.length > 0 && (
        <div className={styles.sectionGroup}>
          <h2 className={styles.sectionHeading}>
            Communities matching "{searchQuery}"
          </h2>
          <div className={styles.communityResultsRow}>
            {filteredCommunities.map((comm) => (
              <div
                key={comm.id}
                className={`${styles.communityCard} ${styles.clickableCard}`}
                onClick={() => {
                  if (onSelectCommunity) onSelectCommunity(comm);
                }}
              >
                <h4 className={styles.communityTitle}>{comm.name}</h4>
                <p className={styles.communityDesc}>{comm.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic View: Displayed when filtering by category or search query */}
      {isFiltering ? (
        <div className={styles.sectionGroup}>
          <h2 className={styles.sectionHeading}>
            {selectedCategory !== "For You"
              ? `Events in ${selectedCategory}`
              : `Search Results for "${searchQuery}"`}
          </h2>

          {filteredEvents.length > 0 ? (
            <>
              {/* Primary featured banner displaying the first filtered match */}
              <div
                onClick={() => onSelectEvent(filteredEvents[0])}
                className={`${styles.featuredBannerCard} ${styles.featuredBannerSpacing}`}
              >
                <img
                  src={
                    filteredEvents[0].image ||
                    "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=500&auto=format&fit=crop&q=60"
                  }
                  alt={filteredEvents[0].title}
                  className={styles.featuredImage}
                />
                <div className={styles.featuredOverlay}></div>
                <span className={styles.featuredCategoryBadge}>
                  {filteredEvents[0].category || "Culture"}
                </span>
                <div className={styles.featuredContent}>
                  <h3 className={styles.featuredTitle}>
                    {filteredEvents[0].title}
                  </h3>
                  <p className={styles.featuredDescription}>
                    {filteredEvents[0].description || filteredEvents[0].desc}
                  </p>
                  <div className={styles.featuredFooter}>
                    <div>
                      <p className={styles.featuredHost}>
                        Hosted by:{" "}
                        <span className={styles.hostName}>
                          {filteredEvents[0].host}
                        </span>
                      </p>
                      <p className={styles.featuredDate}>
                        {filteredEvents[0].date}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remaining filtered events listed in a row below */}
              {filteredEvents.length > 1 && (
                <EventRow
                  title="More in this selection"
                  events={filteredEvents.slice(1)}
                  joinedEvents={joinedEvents}
                  onSelectEvent={onSelectEvent}
                  onToggleJoinEvent={onToggleJoinEvent}
                />
              )}
            </>
          ) : (
            <p className={styles.noEventsText}>
              No events found matching your criteria.
            </p>
          )}
        </div>
      ) : (
        /* Normal View: Default dashboard layout when no filters are active */
        <>
          {/* Popular Events Near Your Area (Featured Banner Section) */}
          {featuredEvent && (
            <div className={styles.sectionGroup}>
              <h2 className={styles.sectionHeading}>
                Popular events near your area
              </h2>

              <div
                onClick={() => onSelectEvent(featuredEvent)}
                className={styles.featuredBannerCard}
              >
                <img
                  src={
                    featuredEvent.image ||
                    "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=500&auto=format&fit=crop&q=60"
                  }
                  alt={featuredEvent.title}
                  className={styles.featuredImage}
                />
                <div className={styles.featuredOverlay}></div>

                <span className={styles.featuredCategoryBadge}>
                  {featuredEvent.category || "Culture"}
                </span>

                <div className={styles.featuredContent}>
                  <h3 className={styles.featuredTitle}>
                    {featuredEvent.title}
                  </h3>
                  <p className={styles.featuredDescription}>
                    {featuredEvent.description || featuredEvent.desc}
                  </p>
                  <div className={styles.featuredFooter}>
                    <div>
                      <p className={styles.featuredHost}>
                        Hosted by:{" "}
                        <span className={styles.hostName}>
                          {featuredEvent.host}
                        </span>
                      </p>
                      <p className={styles.featuredDate}>
                        {featuredEvent.date}
                      </p>
                    </div>

                    <div className={styles.featuredActions}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onToggleJoinEvent)
                            onToggleJoinEvent(featuredEvent.id);
                        }}
                        className={styles.bannerPlusBtn}
                        title={isFeaturedJoined ? "Leave Event" : "Join Event"}
                      >
                        <Plus
                          size={16}
                          className={
                            isFeaturedJoined
                              ? styles.plusIconRotated
                              : styles.plusIconRotatable
                          }
                        />
                      </button>
                      <span className={styles.seeMorePill}>
                        {isFeaturedJoined ? "Joined" : "See more"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Personalized event rows */}
          <EventRow
            title={firstRowTitle}
            events={firstRowEvents}
            joinedEvents={joinedEvents}
            onSelectEvent={onSelectEvent}
            onToggleJoinEvent={onToggleJoinEvent}
          />
          <EventRow
            title={secondRowTitle}
            events={secondRowEvents}
            joinedEvents={joinedEvents}
            onSelectEvent={onSelectEvent}
            onToggleJoinEvent={onToggleJoinEvent}
          />
          <EventRow
            title={thirdRowTitle}
            events={thirdRowEvents}
            joinedEvents={joinedEvents}
            onSelectEvent={onSelectEvent}
            onToggleJoinEvent={onToggleJoinEvent}
          />
          <EventRow
            title={allEventsTitle}
            events={allEvents}
            joinedEvents={joinedEvents}
            onSelectEvent={onSelectEvent}
            onToggleJoinEvent={onToggleJoinEvent}
          />
        </>
      )}
    </div>
  );
}
